/**
 * WalletInstitutions — "My Trusted Institutions" landing page.
 *
 * Trust concepts kept SEPARATE (never a single "verified" badge):
 *   A. Participating Institution   — Altara-participation status
 *   B. Regulatory Validation       — surfaced on the detail page
 *   C. Consumer Relationship       — this list is scoped to the consumer
 *   D. Interaction Provenance      — latest Altara interaction shown per card
 *
 * The list itself is intentionally minimal. Depth lives in the
 * institution-detail page.
 */
import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Building2, ShieldCheck, ArrowRight, Info, Landmark, Sparkles, ExternalLink,
} from "lucide-react";
import { WalletShell, NAVY, GOLD } from "@/components/wallet/WalletShell";
import { useTrustedInstitutions } from "@/lib/wallet-trusted-institutions";
import { loadWallet } from "@/lib/wallet-storage";
import InfoBadge from "@/components/wallet/institutions/InfoBadge";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function WalletInstitutions() {
  const { items, loading, refresh } = useTrustedInstitutions();
  const [seeding, setSeeding] = useState(false);

  async function bootstrapDemo() {
    const w = loadWallet() || {};
    if (!w.device_id || seeding) return;
    setSeeding(true);
    try {
      await axios.post(`${API}/wallet/institutions/bootstrap-demo`, { device_id: w.device_id });
      await refresh();
    } finally { setSeeding(false); }
  }

  return (
    <WalletShell activeTab="institutions">
      <div className="max-w-3xl mx-auto px-4 lg:px-8 py-6 lg:py-8 space-y-6" data-testid="wallet-institutions">
        <header>
          <div className="text-[10px] uppercase tracking-[0.24em] font-semibold" style={{ color: GOLD }}>
            My Trusted Institutions
          </div>
          <h1 className="font-display text-2xl lg:text-3xl font-semibold mt-1 inline-flex items-center gap-2" style={{ color: NAVY }}>
            Your financial relationships
            <InfoBadge
              testid="trusted-info-badge"
              title="What does trusted mean?"
              body="Trusted means the relationship or reference information has been established through Altara. It does not mean Altara endorses an institution, adviser or financial product."
            />
          </h1>
          <p className="text-[13px] text-slate-600 mt-2 max-w-xl leading-relaxed">
            Your financial relationships and trusted reference information in one place.
          </p>
          <p className="text-[12px] text-slate-500 mt-2 max-w-xl leading-relaxed">
            Participating institutions can provide verified relationship and communication information to your Trust Wallet, helping you recognise legitimate interactions and check unexpected ones.
          </p>
        </header>

        {loading ? (
          <div className="rounded-2xl bg-white border border-slate-100 p-8 text-center text-slate-400 animate-pulse" data-testid="institutions-loading">
            Loading your institutions…
          </div>
        ) : items.length === 0 ? (
          <EmptyState onBootstrap={bootstrapDemo} seeding={seeding} />
        ) : (
          <div className="space-y-3">
            {items.map(it => <RelationshipCard key={it.relationship.relationship_id} item={it} />)}
          </div>
        )}

        <FooterNote />
      </div>
    </WalletShell>
  );
}

function RelationshipCard({ item }) {
  const inst   = item.institution;
  const rel    = item.relationship;
  const rep    = (item.representatives || [])[0];
  const latest = item.latest_interaction;
  return (
    <Link
      to={`/wallet/institutions/${inst.institution_id}`}
      data-testid={`inst-card-${inst.institution_id}`}
      className="block rounded-2xl bg-white border border-slate-100 hover:shadow-sm transition p-5"
    >
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${GOLD}22` }}>
          <Building2 className="w-6 h-6" style={{ color: NAVY }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display text-[16px] font-semibold" style={{ color: NAVY }}>
              {inst.trading_name || inst.legal_name}
            </div>
            <ParticipatingBadge />
          </div>

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <MetaCell label="Relationship" value={rel.relationship_type} />
            <MetaCell label="Adviser" value={rep ? rep.name : "—"} />
            <MetaCell label="Latest interaction" value={latest ? latest.proposal_name : "—"} />
          </div>

          <div className="mt-4 inline-flex items-center gap-1 text-[12.5px] font-semibold" style={{ color: NAVY }}>
            View relationship <ArrowRight className="w-3 h-3" />
          </div>
        </div>
      </div>
    </Link>
  );
}

function MetaCell({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{label}</div>
      <div className="text-[13px] font-medium mt-0.5 truncate" style={{ color: NAVY }}>{value}</div>
    </div>
  );
}

export function ParticipatingBadge() {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10.5px] font-semibold"
      style={{ background: "#ECFDF5", color: "#065F46" }}
      data-testid="participating-badge"
      title="This institution participates in Altara and has provided institutional and relationship information to support the Trust Wallet."
    >
      <ShieldCheck className="w-3 h-3" /> Participating Institution
    </span>
  );
}

function EmptyState({ onBootstrap, seeding }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-100 p-8 text-center" data-testid="institutions-empty">
      <div className="w-11 h-11 mx-auto rounded-full flex items-center justify-center mb-3" style={{ background: "#F1F5F9" }}>
        <Landmark className="w-5 h-5 text-slate-400" />
      </div>
      <div className="font-display text-[15px] font-semibold" style={{ color: NAVY }}>
        No institutions connected yet
      </div>
      <div className="text-[12px] text-slate-500 mt-1 leading-relaxed max-w-md mx-auto">
        Once a participating institution establishes a relationship with your Trust Wallet, it will appear here with its trusted reference information.
      </div>
      <button
        onClick={onBootstrap}
        disabled={seeding}
        data-testid="institutions-bootstrap-demo"
        className="mt-5 inline-flex items-center gap-1.5 rounded-full px-5 py-2 text-[12.5px] font-semibold text-white disabled:opacity-50"
        style={{ background: NAVY }}
      >
        <Sparkles className="w-3.5 h-3.5" style={{ color: GOLD }} /> {seeding ? "Setting up demo…" : "Bootstrap the ABC Wealth demo"}
      </button>
      <div className="mt-3">
        <Link
          to="/institution-portal/inst_abc_wealth"
          data-testid="peek-institution-portal"
          className="inline-flex items-center gap-1 text-[11.5px] font-medium underline underline-offset-2"
          style={{ color: NAVY }}
        >
          <ExternalLink className="w-3 h-3" /> See how ABC Wealth's information is collected & verified
        </Link>
      </div>
    </div>
  );
}

function FooterNote() {
  return (
    <div className="rounded-xl p-3 flex items-start gap-2 text-[11px]" style={{ background: "#F1F5F9" }} data-testid="institutions-boundary">
      <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: NAVY }} />
      <div className="text-slate-600 leading-relaxed">
        Altara does not endorse or recommend financial products. This is a reference layer between you and the institutions you already have relationships with.
      </div>
    </div>
  );
}
